import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import {
  listarNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
  etiquetaTipo,
} from "@/data/socioNotifsStore";
import { tiempoRelativo } from "@/data/saldoSocioStore";

/**
 * Campana de notificaciones del Portal del Socio.
 *
 * - Muestra el número de notificaciones no leídas como badge.
 * - Panel desplegable con las últimas 6 notificaciones + acciones.
 * - "Marcar todas como leídas" + link a "/app/notificaciones".
 * - Refresca cada 60 s y al abrir el panel.
 */
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [marcandoTodas, setMarcandoTodas] = useState(false);
  const [items, setItems] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const wrapRef = useRef(null);

  const hidratar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await listarNotificaciones({ limite: 20 });
      setItems(data.items);
      setNoLeidas(data.noLeidas);
    } catch {
      // 401: el AppLayout ya redirige; no molestamos aquí.
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    hidratar();
    const id = setInterval(hidratar, 60_000);
    return () => clearInterval(id);
  }, [hidratar]);

  useEffect(() => {
    if (!open) return undefined;
    hidratar();
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open, hidratar]);

  const clickItem = async (n) => {
    if (!n.leida) {
      try {
        await marcarLeida(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
        setNoLeidas((c) => Math.max(0, c - 1));
      } catch {
        // ignorar; el badge se corregirá en el siguiente tick.
      }
    }
  };

  const clickMarcarTodas = async () => {
    setMarcandoTodas(true);
    try {
      await marcarTodasLeidas();
      setItems((prev) => prev.map((x) => ({ ...x, leida: true })));
      setNoLeidas(0);
    } finally {
      setMarcandoTodas(false);
    }
  };

  const badge = noLeidas > 9 ? "9+" : String(noLeidas);
  const primeras = items.slice(0, 6);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="notif-bell"
        aria-label={`Notificaciones${noLeidas ? `: ${noLeidas} sin leer` : ""}`}
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border hairline bg-card/70 text-foreground hover:bg-muted transition-colors"
      >
        <Bell className="w-4 h-4" strokeWidth={1.7} />
        {noLeidas > 0 ? (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary dark:bg-accent text-primary-foreground dark:text-accent-foreground text-[10px] font-bold grid place-items-center leading-none"
          >
            {badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          data-testid="notif-bell-panel"
          className="absolute right-0 mt-2 w-[92vw] max-w-sm rounded-2xl border hairline bg-card shadow-lg z-50 overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b hairline">
            <p className="font-heading text-sm">Notificaciones</p>
            <button
              type="button"
              onClick={clickMarcarTodas}
              data-testid="notif-bell-marcar-todas"
              disabled={marcandoTodas || noLeidas === 0}
              className="inline-flex items-center gap-1 text-xs text-primary dark:text-accent hover:underline underline-offset-4 disabled:opacity-40 disabled:no-underline"
            >
              {marcandoTodas ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCheck className="w-3.5 h-3.5" />
              )}
              Marcar todas
            </button>
          </header>

          <div className="max-h-[60vh] overflow-y-auto divide-y hairline">
            {cargando && primeras.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
              </div>
            ) : primeras.length === 0 ? (
              <div
                data-testid="notif-bell-vacio"
                className="p-6 text-sm text-muted-foreground text-center"
              >
                Aún no tienes notificaciones.
              </div>
            ) : (
              primeras.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => clickItem(n)}
                  data-testid={`notif-bell-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 flex flex-col gap-1 transition-colors ${
                    n.leida ? "bg-transparent" : "bg-primary/5 dark:bg-accent/10"
                  } hover:bg-muted/60`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="overline text-accent text-[10px]">
                      {etiquetaTipo(n.tipo)}
                    </span>
                    {!n.leida ? (
                      <span
                        aria-hidden
                        className="w-1.5 h-1.5 rounded-full bg-primary dark:bg-accent"
                      />
                    ) : null}
                  </div>
                  <p className="text-sm font-medium leading-snug line-clamp-2">
                    {n.titulo}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {n.mensaje}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {tiempoRelativo(n.fechaCreacion)}
                  </p>
                </button>
              ))
            )}
          </div>

          <footer className="border-t hairline px-4 py-3">
            <Link
              to="/app/notificaciones"
              onClick={() => setOpen(false)}
              data-testid="notif-bell-ver-todas"
              className="block text-center text-xs font-medium text-primary dark:text-accent hover:underline underline-offset-4"
            >
              Ver todas
            </Link>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
